import React, { useState } from "react";
import { Button, Input, Modal, Radio, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { useBulkDeleteEmailDocumentsMutation } from "../../../api/graphql/mutations/deleteEmailDocument";
import { DeletionReason } from "../../../api/graphql/schema.generated";
import {
  destroyMessage,
  showErrorMessage,
  showLoadingMessage,
  showSuccessMessage,
} from "../../../utils";

const LOADING_KEY = "bulk-delete-email-documents";

// Same reason set as the single-delete flow (DeleteEmailDocumentModal). The
// operator picks ONE shared reason that is applied to every selected receipt.
const DELETION_REASON_KEYS = [
  "DUPLICATE",
  "WRONG_RECEIPT",
  "NOT_A_RECEIPT",
  "UPLOADED_BY_MISTAKE",
  "OTHER",
] as const;

interface BulkDeleteEmailDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called once at least one receipt was deleted so the parent can clear the
   * selection and refetch. */
  onSuccess: () => void;
  emailDocumentIds: string[];
}

/**
 * Bulk-delete confirmation for the Receipt Overview (SMT-997). Makes a single
 * `bulkDeleteEmailDocuments` call (mirroring VerificationView's
 * `bulkDeleteExternalTransactions`) with one shared reason for all selected
 * receipts; the backend returns the count actually deleted.
 */
const BulkDeleteEmailDocumentModal: React.FC<
  BulkDeleteEmailDocumentModalProps
> = ({ isOpen, onClose, onSuccess, emailDocumentIds }) => {
  const { t } = useTranslation("DeleteEmailDocumentModal");
  const [selectedReason, setSelectedReason] = useState<
    DeletionReason | undefined
  >(undefined);
  const [reasonText, setReasonText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [bulkDeleteEmailDocuments] = useBulkDeleteEmailDocumentsMutation();

  const count = emailDocumentIds.length;

  const isConfirmEnabled =
    !!selectedReason &&
    (selectedReason !== "OTHER" || reasonText.trim().length > 0);

  const resetState = () => {
    setSelectedReason(undefined);
    setReasonText("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedReason) return;

    setIsLoading(true);
    showLoadingMessage(LOADING_KEY);

    const deletionReasonText =
      selectedReason === "OTHER" ? reasonText.trim() : undefined;

    let deletedCount = 0;
    try {
      const { data } = await bulkDeleteEmailDocuments({
        variables: {
          ids: emailDocumentIds,
          deletionReason: selectedReason,
          deletionReasonText,
        },
      });
      deletedCount = data?.bulkDeleteEmailDocuments ?? 0;
    } catch {
      deletedCount = 0;
    }

    const failedCount = count - deletedCount;

    destroyMessage(LOADING_KEY);
    if (deletedCount === count) {
      showSuccessMessage(t("bulkSuccess", { count }));
    } else if (deletedCount === 0) {
      showErrorMessage(t("bulkError", { count }));
    } else {
      showErrorMessage(
        t("bulkPartialError", { failed: failedCount, total: count })
      );
    }

    setIsLoading(false);

    // Hand back and reset once at least one delete landed; on a total failure
    // keep the modal open so the operator can retry.
    if (deletedCount > 0) {
      resetState();
      onSuccess();
    }
  };

  return (
    <Modal
      open={isOpen}
      title={t("bulkTitle", { count })}
      onCancel={handleClose}
      // Block ESC / mask dismissal while the bulk delete is in flight so the
      // operator can't "cancel" a modal whose mutations are still resolving.
      closable={!isLoading}
      maskClosable={!isLoading}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={isLoading}>
          {t("cancel")}
        </Button>,
        <Button
          key="confirm"
          danger
          type="primary"
          disabled={!isConfirmEnabled}
          loading={isLoading}
          onClick={handleConfirm}
        >
          {t("bulkConfirm", { count })}
        </Button>,
      ]}
    >
      <Typography.Paragraph type="secondary">
        {t("bulkSubtitle", { count })}
      </Typography.Paragraph>
      <Radio.Group
        value={selectedReason}
        onChange={(e) => setSelectedReason(e.target.value)}
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        {DELETION_REASON_KEYS.map((key) => (
          <Radio key={key} value={key}>
            {t(`reasons.${key}`)}
          </Radio>
        ))}
      </Radio.Group>
      {selectedReason === "OTHER" && (
        <Input.TextArea
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value)}
          placeholder={t("placeholder") as string}
          rows={3}
          style={{ marginTop: 12 }}
        />
      )}
    </Modal>
  );
};

export default BulkDeleteEmailDocumentModal;
